import React, { useState, useEffect, useRef } from "react"
import { initializeApp } from "firebase/app"
import {
  getFirestore,
  collection,
  addDoc,
  serverTimestamp,
  query,
  orderBy,
  doc,
  updateDoc,
} from "firebase/firestore"
import {
  useDocumentData,
  useCollectionData,
} from "react-firebase-hooks/firestore"
import { Helpers } from "@flexday/common-react"
import { Utils } from "@flexday/common"
import { trackEvent } from "@intercom/messenger-js-sdk"
import ReactMarkdown from "react-markdown"
import { PaperAirplaneIcon as SendIcon } from "@heroicons/react/solid"

const TypingIndicator = () => (
  <div className="flex justify-start mb-2">
    <div className="bg-white border border-gray-200 p-3 rounded-lg">
      <div className="flex space-x-1">
        <div
          className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
          style={{ animationDelay: "0ms" }}
        />
        <div
          className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
          style={{ animationDelay: "150ms" }}
        />
        <div
          className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
          style={{ animationDelay: "300ms" }}
        />
      </div>
    </div>
  </div>
)

const BookingAgent = ({ settingsId, autoJoin = false }) => {
  const [conversationRef, setConversationRef] = useState(null)
  const [conversation, conversationLoading, conversationError] =
    useDocumentData(conversationRef)
  const [newMessage, setNewMessage] = useState("")
  const messagesEndRef = useRef(null)
  const chatContainerRef = useRef(null)

  const { getFirebaseConfig } = Helpers
  const firebaseConfig = getFirebaseConfig({
    REACT_APP_FIREBASE_API_KEY: process.env.REACT_APP_FIREBASE_API_KEY,
    REACT_APP_FIREBASE_APP_ID: process.env.REACT_APP_FIREBASE_APP_ID,
    REACT_APP_FIREBASE_MESSAGE_SENDER_ID:
      process.env.REACT_APP_FIREBASE_MESSAGE_SENDER_ID,
    REACT_APP_FIREBASE_PROJECT_ID: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  })

  const app = initializeApp(firebaseConfig)
  const firestore = getFirestore(app)

  // Fetch settings from Firestore
  const settingsRef = settingsId ? doc(firestore, "settings", settingsId) : null
  const [settings, settingsLoading, settingsError] =
    useDocumentData(settingsRef)

  const messagesRef = conversationRef && collection(conversationRef, "messages")
  const messagesQuery = messagesRef && query(messagesRef, orderBy("createdAt"))

  const [messages, messagesLoading, messagesError] = useCollectionData(
    messagesQuery,
    {
      idField: "id",
    }
  )

  const conversationKey = `booking_conversation_${
    new Date().toISOString().split("T")[0]
  }`

  const saveConversation = (conversationId) => {
    localStorage.setItem(conversationKey, conversationId)
  }

  const loadConversationId = () => {
    return localStorage.getItem(conversationKey)
  }

  const onConnect = async () => {
    const savedConversationId = loadConversationId()
    if (savedConversationId) {
      setConversationRef(doc(firestore, "conversations", savedConversationId))
    } else {
      const newConversationRef = await addDoc(
        collection(firestore, "conversations"),
        {
          type: "workspace_booking",
          createdAt: serverTimestamp(),
          agentReplying: true,
          status: "active",
          settings: doc(firestore, "settings", settingsId),
        }
      )
      setConversationRef(newConversationRef)
      saveConversation(newConversationRef.id)
    }
  }

  useEffect(() => {
    const savedConversationId = loadConversationId()
    if (savedConversationId) {
      setConversationRef(doc(firestore, "conversations", savedConversationId))
    }
  }, [])

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const urlAutoJoinEnabled = urlParams.get("autoJoin") === "true"
    const existingConversationId = loadConversationId()

    // Auto-join if enabled via URL params OR via data attribute
    if ((urlAutoJoinEnabled || autoJoin) && !existingConversationId) {
      onConnect()
    }
  }, [autoJoin])

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !messagesRef) return

    await addDoc(messagesRef, {
      content: newMessage,
      role: "user",
      createdAt: serverTimestamp(),
    })
    setNewMessage("")
  }

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight
    }
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages?.length, conversation?.agentReplying])

  const handleEndConversation = async () => {
    if (conversationRef) {
      await updateDoc(conversationRef, {
        status: "closed",
        closedAt: serverTimestamp(),
      })
    }
    localStorage.removeItem(conversationKey)
  }

  if (!settings) {
    return null
  }

  const isConversationClosed = conversation?.status === "closed"

  return (
    <div className="flex flex-col items-center gap-4 bg-white p-4 rounded-[20px]">
      {!conversation && !conversationLoading && !autoJoin && (
        <div className="w-96">
          <button
            className="self-center mt-2 w-full py-2 hover:opacity-75 transition-opacity px-16"
            style={settings?.buttonStyles}
            onClick={onConnect}
            disabled={conversationLoading || settingsLoading}>
            {settings.bookWorkspaceCTA}
          </button>
        </div>
      )}
      {conversation && (
        <div className="flex flex-col w-full h-[500px]">
          <div
            className="flex-1 overflow-y-auto p-4 bg-gray-50 rounded-t-lg"
            ref={chatContainerRef}>
            {messagesLoading || settingsLoading ? (
              <div className="text-center text-gray-500">
                Loading messages...
              </div>
            ) : messagesError || settingsError ? (
              <div className="text-center text-red-500">
                Error loading messages
              </div>
            ) : (
              <>
                {messages?.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${
                      message.role === "assistant"
                        ? "justify-start"
                        : "justify-end"
                    } mb-2`}>
                    <div
                      className={`max-w-[80%] p-3 rounded-lg ${
                        message.role === "assistant"
                          ? "bg-white border border-gray-200"
                          : ""
                      }`}
                      style={
                        message.role === "user"
                          ? {
                              backgroundColor: settings?.accentColor,
                              color: settings?.accentFontColor,
                            }
                          : {}
                      }>
                      <div className="prose prose-sm max-w-none">
                        <ReactMarkdown
                          components={{
                            p: ({ node, ...props }) => (
                              <p className="mb-4" {...props} />
                            ),
                            ul: ({ node, ...props }) => (
                              <ul className="mb-4" {...props} />
                            ),
                            hr: ({ node, ...props }) => (
                              <hr className="mb-4" {...props} />
                            ),
                          }}>
                          {message.content}
                        </ReactMarkdown>
                      </div>
                      <span className="text-xs text-gray-500 mt-1 block">
                        {message.createdAt?.toDate().toLocaleTimeString()}
                      </span>
                    </div>
                  </div>
                ))}
                {conversation?.agentReplying && !isConversationClosed && (
                  <TypingIndicator />
                )}
                {isConversationClosed && (
                  <div className="text-center mt-4">
                    <span className="text-gray-600 text-sm">
                      Conversation ended at{" "}
                      {conversation.closedAt?.toDate().toLocaleTimeString([], {
                        hour: "numeric",
                        minute: "2-digit",
                        hour12: true,
                      })}
                    </span>
                  </div>
                )}
              </>
            )}
            <div ref={messagesEndRef} />
          </div>
          <div className="flex gap-2 w-full p-4 bg-white border-t border-gray-200 rounded-b-lg">
            <textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your message..."
              className={`flex-1 p-2 border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[${
                settings.accentColor
              }] focus:border-transparent ${
                isConversationClosed ? "bg-gray-100 cursor-not-allowed" : ""
              }`}
              rows={2}
              disabled={isConversationClosed}
            />
            <div className="w-12">
              <button
                className={`w-full h-full flex items-center justify-center self-end rounded-lg transition-opacity ${
                  isConversationClosed
                    ? "cursor-not-allowed opacity-50"
                    : "hover:opacity-75"
                }`}
                style={{
                  backgroundColor: settings?.accentColor,
                  color: settings?.accentFontColor,
                }}
                onClick={handleSendMessage}
                disabled={!newMessage.trim() || isConversationClosed}>
                <SendIcon className="w-4 rotate-90" />
              </button>
            </div>
          </div>
          {!isConversationClosed && (
            <button
              onClick={handleEndConversation}
              className="text-sm text-gray-500 hover:text-gray-700 transition-colors self-center mt-2">
              End Conversation
            </button>
          )}
        </div>
      )}
    </div>
  )
}

export default BookingAgent
